// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	extractResourceStatePromise,
	getAssetsTenantContextMetadata,
} from '@jira/router-resources__assets-admin-settings/src/extractResourceStatePromise.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { getResourceStore, type ResourceStoreContext } from '@atlassian/react-resource-router';
import {
	transformAssetsPermissionResponse,
	getAssetsPermissionUrl,
	getAssetsPermissionHeaders,
} from './getAssetsPermissionHeaders.tsx';
import type {
	AssetsPermissionResponse,
	AssetsPermissionsApiResponse,
} from './AssetsPermissionResponse.types.tsx';
import { emptyAssetsPermissionResponse } from './emptyAssetsPermissionResponse.constants.tsx';

const packageName = 'jiraRouterResourcesAssetsUserPermission';
const teamName = 'astral';

/**
 * Shared service to check if a user has Assets permissions
 * Used by both the router resource and utility functions
 */

export const checkAssetsPermission = async ({
	tenantContext,
}: ResourceStoreContext): Promise<AssetsPermissionResponse> => {
	const { storeState } = getResourceStore();
	const state = storeState.getState();

	try {
		const workspaceId = await extractResourceStatePromise(state.data);

		if (!workspaceId || typeof workspaceId !== 'string') {
			return emptyAssetsPermissionResponse;
		}

		const { cloudId } = tenantContext;

		const response = await performGetRequest<AssetsPermissionsApiResponse>(
			getAssetsPermissionUrl(workspaceId),
			{
				headers: getAssetsPermissionHeaders(workspaceId, cloudId),
			},
		);

		return transformAssetsPermissionResponse(response, true);

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const tenantContextMetadata = getAssetsTenantContextMetadata();

		logger.safeErrorWithoutCustomerData(`${packageName}.checkAssetsUserPermission`, error.message, {
			statusCode: error?.statusCode,
			errorName: error?.name,
			description: error?.originalResponse?.message,
			tenantContextMetadata: JSON.stringify(tenantContextMetadata || {}),
		});

		fireErrorAnalytics({
			meta: {
				id: 'checkAssetsUserPermission',
				packageName,
				teamName,
			},
			error,
		});
		throw error;
	}
};

export default checkAssetsPermission;
